import React, { useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField, Paper, Typography, Box, Grid, Button, CircularProgress, Alert, MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import NavBar from "../navbar";
import config from "../config";
import * as Sentry from "@sentry/react";

const CreateRestriction = () => {
    const navigate = useNavigate();
    const [data, setData] = useState({
        endpoint_id: "",
        plan_id: "",
        ten_second_limit: null,
        minutely_limit: null,
        hourly_limit: null,
        daily_limit: null,
        monthly_limit: null,
        total_limit: null,
        price: 1,
        max_results: null,
        increase_every: null,
        disabled: false,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) return validate_logged_in;

    const handleChange = (field, value) => setData({ ...data, [field]: value });

    const handleSave = async () => {
        try {
            setSaving(true); setError(null);
            if (!data.endpoint_id.trim()) { setError("Endpoint ID is required"); setSaving(false); return; }
            if (!data.plan_id.trim()) { setError("Plan ID is required"); setSaving(false); return; }

            const payload = {
                endpoint_id: data.endpoint_id,
                plan_id: data.plan_id,
                ten_second_limit: data.ten_second_limit,
                minutely_limit: data.minutely_limit,
                hourly_limit: data.hourly_limit,
                daily_limit: data.daily_limit,
                monthly_limit: data.monthly_limit,
                total_limit: data.total_limit,
                price: parseInt(data.price),
                max_results: data.max_results !== null ? parseInt(data.max_results) : null,
                increase_every: data.increase_every !== null ? parseInt(data.increase_every) : null,
                disabled: data.disabled,
            };
            const response = await fetch(config.apiBaseUrl + "/admin/endpoint-restrictions", {
                method: "POST",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
                body: JSON.stringify(payload),
            });
            if (!response.ok) { const err = await response.json(); throw new Error(err.detail || `Error: ${response.status}`); }
            setSuccess(true);
            setTimeout(() => navigate("/endpoint-restrictions"), 1500);
        } catch (err) {
            Sentry.captureException(err); setError(err.message);
        } finally { setSaving(false); }
    };

    const limitField = (label, field) => (
        <Grid item xs={12} md={4}>
            <TextField fullWidth margin="normal" label={label} type="number" inputProps={{ min: 1 }}
                value={data[field] === null ? "" : data[field]}
                onChange={(e) => handleChange(field, e.target.value === "" ? null : parseInt(e.target.value) || null)}
                disabled={saving} helperText="Empty = no limit" />
        </Grid>
    );

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 900, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>Create Endpoint Restriction</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>Restriction created!</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth required margin="normal" label="Endpoint ID" value={data.endpoint_id} onChange={(e) => handleChange("endpoint_id", e.target.value)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth required margin="normal" label="Plan ID" value={data.plan_id} onChange={(e) => handleChange("plan_id", e.target.value)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Price" type="number" inputProps={{ min: 1 }} value={data.price} onChange={(e) => handleChange("price", parseInt(e.target.value) || 1)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Max Results" type="number" value={data.max_results === null ? "" : data.max_results} onChange={(e) => handleChange("max_results", e.target.value === "" ? null : parseInt(e.target.value) || null)} disabled={saving} helperText="Empty = no limit" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Increase Every" type="number" inputProps={{ min: 1 }} value={data.increase_every === null ? "" : data.increase_every} onChange={(e) => handleChange("increase_every", e.target.value === "" ? null : parseInt(e.target.value) || null)} disabled={saving} helperText="Empty = no increase" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField select fullWidth margin="normal" label="Disabled" value={data.disabled ? "true" : "false"} onChange={(e) => handleChange("disabled", e.target.value === "true")} disabled={saving}>
                                <MenuItem value="false">No</MenuItem>
                                <MenuItem value="true">Yes</MenuItem>
                            </TextField>
                        </Grid>
                        {limitField("10s Limit", "ten_second_limit")}
                        {limitField("Minutely Limit", "minutely_limit")}
                        {limitField("Hourly Limit", "hourly_limit")}
                        {limitField("Daily Limit", "daily_limit")}
                        {limitField("Monthly Limit", "monthly_limit")}
                        {limitField("Total Limit", "total_limit")}
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} startIcon={saving && <CircularProgress size={16} />}>
                            {saving ? "Saving..." : "Create Restriction"}
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => navigate("/endpoint-restrictions")} disabled={saving}>Cancel</Button>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default CreateRestriction;
